// frontend/src/components/DashboardContabilidade.js
// =============================================
// DASHBOARD DE CONTABILIDADE
// ✅ Fecho de período com data correta (nunca 1/1/1970)
// ✅ Badge "Período já fechado em <data>"
// ✅ Botão "Registar Submissão às Finanças" (com upload de comprovativo)
// ✅ Modal completo para registo de submissão
// ✅ Visualização de comprovativo já existente
// ✅ PERSONALIZAÇÃO DE MOEDA E FORMATAÇÃO DINÂMICA
// 🔥 CORRIGIDO: configuracao + empresa vêm diretamente do backend
//    (resumo, comparativo-anual, fechos) — sem chamadas duplicadas
// 🔥 SINCRONIZADO COM ContabilidadeIVA:
//    - Regime Normal (16%): fecho mensal
//    - Regime ISPC (15%): fecho trimestral (Mar/Jun/Set/Dez)
//    - Regime Isento: sem fecho
// 🔥 MODAL DE SUBMISSÃO ADAPTA-SE AO TIPO DE IMPOSTO DA EMPRESA
// 🔥 POPUPS DO SISTEMA (substituem alert/confirm nativos do navegador)
// 🔥 BOTÃO DE SUBMISSÃO COM ESTADOS DINÂMICOS
// 🔥 ÍCONES: usa <Icon /> do SharedComponents
// 🔥 CORREÇÃO: Cards + tabela mostram o valor REAL do imposto por trimestre
//    - Busca fechos do ano (endpoint /api/contabilidade/fechos)
//    - Se fecho existe → usa o valor registado no backend
//    - Senão → usa o cálculo teórico (15% × Volume)
// 🔥 BLOCO 5 — COMPRAS A FORNECEDOR integradas:
//    - Nova secção "Compras a Fornecedor" com KPIs
//    - Apuramento IVA decompõe: Despesas + Stock + Compras Fornecedor
//    - Gráfico de evolução inclui linha de Compras
//    - Detalhe de Compras por Categoria (usa `d.compras`)
// =============================================
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell
} from 'recharts';
import { Icon } from './SharedComponents';
import {
    FaFilePdf, FaExternalLinkAlt
} from 'react-icons/fa';
import './DashboardContabilidade.css';

const API = '/api' || 'http://localhost:3000';

const meses = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];

const CORES = ['#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#10b981'];

// 🔥 Cores específicas para Compras
const CORES_COMPRAS = ['#0ea5e9', '#14b8a6', '#f97316', '#a855f7', '#06b6d4'];

const MESES_FIM_TRIMESTRE = [2, 5, 8, 11];

const trimestreDoMes = (mes) => Math.floor(mes / 3) + 1;
const isMesFimTrimestre = (mes) => MESES_FIM_TRIMESTRE.includes(mes);

const TIPO_IMPOSTO_POR_REGIME = {
    Normal: 'IVA',
    ISPC: 'ISPC',
    Isento: 'Nenhum'
};

const DESCRICAO_IMPOSTO = {
    IVA: 'Imposto sobre o Valor Acrescentado',
    ISPC: 'Imposto Simplificado para Pequenos Contribuintes',
    Nenhum: 'Sem imposto associado'
};

const PERIODICIDADE_POR_REGIME = {
    Normal: 'Mensal',
    ISPC: 'Trimestral',
    Isento: '—'
};

const TAXA_ISPC = 0.15;
const TAXA_ISPC_LABEL = '15%';

// 🔥 CONFIGURAÇÃO PADRÃO (fallback)
const CONFIG_PADRAO = {
    regimeIVA: 'Normal',
    percentagemIVA: 16,
    moeda: {
        simbolo: 'MT',
        codigo: 'MZN',
        casasDecimais: 2,
        separadorDecimal: ',',
        separadorMilhar: '.',
        posicaoSimbolo: 'depois',
        nome: 'Metical'
    },
    formatacao: {
        data: { formato: 'DD/MM/YYYY', separador: '/', incluirHora: false },
        numero: { separadorDecimal: ',', separadorMilhar: '.', casasDecimais: 2 }
    }
};

// 🔥 Estados possíveis da submissão
const ESTADOS_SUBMISSAO = {
    SEM_SUBMISSAO: 'sem_submissao',
    PENDENTE: 'pendente',
    SUBMETIDO: 'submetido',
    PAGO_SEM_COMPROVATIVO: 'pago_sem_comprovativo',
    PAGO_COMPLETO: 'pago_completo'
};

const ESTADO_TITULO_MODAL = {
    [ESTADOS_SUBMISSAO.SEM_SUBMISSAO]: 'Registar Submissão às Finanças',
    [ESTADOS_SUBMISSAO.PENDENTE]: 'Editar Submissão Pendente',
    [ESTADOS_SUBMISSAO.SUBMETIDO]: 'Editar Submissão Pendente',
    [ESTADOS_SUBMISSAO.PAGO_SEM_COMPROVATIVO]: 'Anexar Comprovativo de Pagamento',
    [ESTADOS_SUBMISSAO.PAGO_COMPLETO]: 'Submissão Completa'
};

const ESTADO_ICONE_MODAL = {
    [ESTADOS_SUBMISSAO.SEM_SUBMISSAO]: 'add_box',
    [ESTADOS_SUBMISSAO.PENDENTE]: 'edit',
    [ESTADOS_SUBMISSAO.SUBMETIDO]: 'edit',
    [ESTADOS_SUBMISSAO.PAGO_SEM_COMPROVATIVO]: 'cloud_upload',
    [ESTADOS_SUBMISSAO.PAGO_COMPLETO]: 'verified'
};

const ESTADO_BADGE_MAP = {
    pendente: { label: 'Pendente', classe: 'badge-warning', icone: 'schedule' },
    submetido: { label: 'Submetido', classe: 'badge-info', icone: 'send' },
    pago_sem_comprovativo: { label: 'Falta Comprovativo', classe: 'badge-warning', icone: 'cloud_upload' },
    pago_completo: { label: 'Completo', classe: 'badge-success', icone: 'verified' },
    sem_submissao: { label: 'Sem Submissão', classe: 'badge-muted', icone: 'hourglass_empty' }
};

export default function DashboardContabilidade() {
    const [ano, setAno] = useState(new Date().getFullYear());
    const [mes, setMes] = useState(new Date().getMonth());
    const [tipo, setTipo] = useState('Mensal');
    const [dados, setDados] = useState(null);
    const [comparativo, setComparativo] = useState([]);
    const [fechos, setFechos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [fechando, setFechando] = useState(false);

    // 🔥 Estado dos popups do sistema
    const [popup, setPopup] = useState({
        aberto: false,
        tipo: 'info',
        titulo: '',
        mensagem: '',
        textoConfirmar: 'Confirmar',
        textoCancelar: 'Cancelar',
        onConfirmar: null,
        onCancelar: null
    });

    const fecharPopup = useCallback(() => {
        setPopup(prev => ({ ...prev, aberto: false }));
    }, []);

    const mostrarPopup = useCallback((tipo, titulo, mensagem) => {
        setPopup({
            aberto: true,
            tipo,
            titulo,
            mensagem,
            textoConfirmar: 'OK',
            textoCancelar: null,
            onConfirmar: () => fecharPopup(),
            onCancelar: null
        });
    }, [fecharPopup]);

    const confirmarPopup = useCallback((titulo, mensagem, onConfirmar, opcoes = {}) => {
        setPopup({
            aberto: true,
            tipo: opcoes.tipo || 'confirm',
            titulo,
            mensagem,
            textoConfirmar: opcoes.textoConfirmar || 'Confirmar',
            textoCancelar: opcoes.textoCancelar || 'Cancelar',
            onConfirmar: () => {
                fecharPopup();
                if (onConfirmar) onConfirmar();
            },
            onCancelar: () => {
                fecharPopup();
                if (opcoes.onCancelar) opcoes.onCancelar();
            }
        });
    }, [fecharPopup]);

    // 🔥 Config da empresa — começa com padrão, é atualizada pelas respostas
    const [empresaConfig, setEmpresaConfig] = useState(CONFIG_PADRAO);

    const [modalSubmissaoAberto, setModalSubmissaoAberto] = useState(false);
    const [submissaoForm, setSubmissaoForm] = useState({
        referencia: '',
        dataSubmissao: new Date().toISOString().slice(0, 10),
        dataPagamento: '',
        valorPago: '',
        estado: 'Submetido',
        observacoes: ''
    });
    const [submissaoFicheiro, setSubmissaoFicheiro] = useState(null);
    const [submetendo, setSubmetendo] = useState(false);

    const token = localStorage.getItem('token');
    const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

    // =============================================
    // 🔥 DERIVADOS DO REGIME
    // =============================================
    const regimeIVA = empresaConfig.regimeIVA || 'Normal';
    const isNormal = regimeIVA === 'Normal';
    const isISPC = regimeIVA === 'ISPC';
    const isIsento = regimeIVA === 'Isento';

    const tipoImposto = TIPO_IMPOSTO_POR_REGIME[regimeIVA] || 'IVA';
    const descricaoImposto = DESCRICAO_IMPOSTO[tipoImposto] || '';
    const periodicidadeImposto = PERIODICIDADE_POR_REGIME[regimeIVA] || 'Mensal';

    const periodicidadeLabel = isISPC ? 'Trimestral' : isNormal ? 'Mensal' : 'Isento';
    const taxaLabel = isNormal
        ? `${empresaConfig.percentagemIVA ?? 16}%`
        : isISPC
            ? `${TAXA_ISPC_LABEL} (trimestral)`
            : '—';

    const trimestreAtual = trimestreDoMes(mes);
    const deveSubmeterEsteMes = isNormal || (isISPC && isMesFimTrimestre(mes));

    useEffect(() => {
        if (isISPC && !isMesFimTrimestre(mes)) {
            const proximoFecho = MESES_FIM_TRIMESTRE.find(m => m >= mes);
            if (proximoFecho !== undefined) {
                setMes(proximoFecho);
            }
        }
    }, [isISPC, mes]);

    // =============================================
    // 🔥 HELPERS — fechos por período
    // =============================================
    const getFechoPorMes = useCallback((mesIdx) => {
        return fechos.find(f => Number(f.mes) === mesIdx && Number(f.ano) === ano);
    }, [fechos, ano]);

    const getFechoPorTrimestre = useCallback((trimestre) => {
        const mesInicio = (trimestre - 1) * 3;
        const mesFim = trimestre * 3 - 1;
        return fechos.find(f => {
            const m = Number(f.mes);
            return m >= mesInicio && m <= mesFim && Number(f.ano) === ano;
        });
    }, [fechos, ano]);

    const estadoDoFecho = useCallback((fecho) => {
        if (!fecho) return ESTADOS_SUBMISSAO.SEM_SUBMISSAO;
        const sub = fecho.submissaoFinancas;
        if (!sub || !sub.estado) return ESTADOS_SUBMISSAO.SEM_SUBMISSAO;

        const estadoLower = String(sub.estado).toLowerCase();
        const temComprovativo = !!sub.comprovativoUrl;

        if (estadoLower === 'pago' && temComprovativo) return ESTADOS_SUBMISSAO.PAGO_COMPLETO;
        if (estadoLower === 'pago' && !temComprovativo) return ESTADOS_SUBMISSAO.PAGO_SEM_COMPROVATIVO;
        if (estadoLower === 'submetido') return ESTADOS_SUBMISSAO.SUBMETIDO;
        if (estadoLower === 'pendente') return ESTADOS_SUBMISSAO.PENDENTE;
        return ESTADOS_SUBMISSAO.PENDENTE;
    }, []);

    const estadoVisual = useCallback((estado) => {
        if (estado === ESTADOS_SUBMISSAO.PAGO_COMPLETO) return ESTADO_BADGE_MAP.pago_completo;
        if (estado === ESTADOS_SUBMISSAO.PAGO_SEM_COMPROVATIVO) return ESTADO_BADGE_MAP.pago_sem_comprovativo;
        if (estado === ESTADOS_SUBMISSAO.SUBMETIDO) return ESTADO_BADGE_MAP.submetido;
        if (estado === ESTADOS_SUBMISSAO.PENDENTE) return ESTADO_BADGE_MAP.pendente;
        return ESTADO_BADGE_MAP.sem_submissao;
    }, []);

    // =============================================
    // 🔥 IMPOSTO REAL DE UM TRIMESTRE (ISPC)
    // =============================================
    const getImpostoTrimestreISPC = useCallback((trimestre) => {
        const fecho = getFechoPorTrimestre(trimestre);
        const mesesIdx = [trimestre * 3 - 3, trimestre * 3 - 2, trimestre * 3 - 1];
        const volumeTri = comparativo
            .filter((_, idx) => mesesIdx.includes(idx))
            .reduce((s, m) => s + (m.faturado || 0), 0);

        if (fecho) {
            const ap = fecho.apuramentoIVA || {};
            if (typeof ap.ivaAEntregar === 'number' && ap.ivaAEntregar > 0) {
                return { imposto: ap.ivaAEntregar, volume: volumeTri, fecho, isRegistado: true };
            }
            const volumeFecho = fecho.receitas?.baseTributavel || volumeTri;
            return { imposto: volumeFecho * TAXA_ISPC, volume: volumeFecho, fecho, isRegistado: false };
        }

        return { imposto: volumeTri * TAXA_ISPC, volume: volumeTri, fecho: null, isRegistado: false };
    }, [comparativo, getFechoPorTrimestre]);

    // =============================================
    // 🔥 ESTADO DA SUBMISSÃO ATUAL
    // =============================================
    const estadoSubmissao = useMemo(() => {
        const sub = dados?.fechoExistente?.submissaoFinancas;
        if (!sub || !sub.estado) return ESTADOS_SUBMISSAO.SEM_SUBMISSAO;

        const estadoLower = String(sub.estado).toLowerCase();
        const temComprovativo = !!sub.comprovativoUrl;

        if (estadoLower === 'pago' && temComprovativo) return ESTADOS_SUBMISSAO.PAGO_COMPLETO;
        if (estadoLower === 'pago' && !temComprovativo) return ESTADOS_SUBMISSAO.PAGO_SEM_COMPROVATIVO;
        if (estadoLower === 'submetido') return ESTADOS_SUBMISSAO.SUBMETIDO;
        if (estadoLower === 'pendente') return ESTADOS_SUBMISSAO.PENDENTE;
        return ESTADOS_SUBMISSAO.PENDENTE;
    }, [dados]);

    const submissaoCompleta = estadoSubmissao === ESTADOS_SUBMISSAO.PAGO_COMPLETO;
    const faltaComprovativo = estadoSubmissao === ESTADOS_SUBMISSAO.PAGO_SEM_COMPROVATIVO;

    // =============================================
    // 🔥 CONFIG VISUAL DO BOTÃO DE SUBMISSÃO
    // =============================================
    const botaoSubmissaoConfig = useMemo(() => {
        if (isIsento) {
            return {
                texto: 'Registar Submissão',
                icone: 'add_box',
                iconeAlerta: null,
                classe: 'btn-submissao',
                disabled: true,
                title: 'Empresa isenta — sem submissão de IVA'
            };
        }

        if (!dados?.fechoExistente?._id) {
            return {
                texto: 'Registar Submissão',
                icone: 'add_box',
                iconeAlerta: null,
                classe: 'btn-submissao',
                disabled: true,
                title: 'Feche o período primeiro'
            };
        }

        if (submissaoCompleta) {
            return {
                texto: 'Submissão Completa',
                icone: 'verified',
                iconeAlerta: null,
                classe: 'btn-submissao btn-submissao-completa',
                disabled: true,
                title: 'Submissão registada e paga com comprovativo. Não pode ser editada.'
            };
        }

        if (faltaComprovativo) {
            return {
                texto: 'Falta Comprovativo',
                icone: 'cloud_upload',
                iconeAlerta: 'warning',
                classe: 'btn-submissao btn-submissao-pago-sem-doc',
                disabled: false,
                title: 'Submissão marcada como paga — anexe o comprovativo para completar.'
            };
        }

        if (estadoSubmissao === ESTADOS_SUBMISSAO.PENDENTE
            || estadoSubmissao === ESTADOS_SUBMISSAO.SUBMETIDO) {
            return {
                texto: 'Editar Submissão Pendente',
                icone: 'edit',
                iconeAlerta: null,
                classe: 'btn-submissao btn-submissao-editar',
                disabled: false,
                title: 'Editar submissão pendente'
            };
        }

        return {
            texto: 'Registar Submissão',
            icone: 'add_box',
            iconeAlerta: null,
            classe: 'btn-submissao',
            disabled: false,
            title: `Registar submissão de ${tipoImposto} às Finanças`
        };
    }, [isIsento, dados, submissaoCompleta, faltaComprovativo, estadoSubmissao, tipoImposto]);

    const modalTitulo = ESTADO_TITULO_MODAL[estadoSubmissao] || 'Registar Submissão às Finanças';
    const modalIcone = ESTADO_ICONE_MODAL[estadoSubmissao] || 'add_box';

    // =============================================
    // 🔥 FORMATAR MOEDA (DINÂMICA)
    // =============================================
    const formatarMoeda = useMemo(() => {
        return (valor) => {
            const cfg = empresaConfig.moeda || CONFIG_PADRAO.moeda;
            const sepDec = cfg.separadorDecimal || ',';
            const sepMil = cfg.separadorMilhar || '.';
            const casasDec = cfg.casasDecimais ?? 2;
            const simbolo = cfg.simbolo || 'MT';

            if (valor === undefined || valor === null || isNaN(valor)) {
                const zeros = '0'.repeat(casasDec);
                return `0${sepDec}${zeros} ${simbolo}`;
            }

            let valorFormatado = Number(valor).toFixed(casasDec);
            valorFormatado = valorFormatado.replace('.', sepDec);

            const partes = valorFormatado.split(sepDec);
            partes[0] = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, sepMil);
            valorFormatado = partes.join(sepDec);

            return `${valorFormatado} ${simbolo}`;
        };
    }, [empresaConfig.moeda]);

    const formatarMoedaLocal = formatarMoeda;

    // =============================================
    // 🔥 FORMATAR DATA (DINÂMICA)
    // =============================================
    const formatarData = useCallback((data) => {
        if (!data) return '-';
        const d = new Date(data);
        if (isNaN(d.getTime())) return '-';

        const cfg = empresaConfig.formatacao?.data || CONFIG_PADRAO.formatacao.data;
        let formato = cfg.formato || 'DD/MM/YYYY';
        const dia = String(d.getDate()).padStart(2, '0');
        const mesNum = String(d.getMonth() + 1).padStart(2, '0');
        const anoNum = d.getFullYear();

        formato = formato.replace('DD', dia);
        formato = formato.replace('MM', mesNum);
        formato = formato.replace('YYYY', anoNum);
        formato = formato.replace('YY', String(anoNum).slice(-2));

        if (cfg.incluirHora) {
            const horas = String(d.getHours()).padStart(2, '0');
            const minutos = String(d.getMinutes()).padStart(2, '0');
            return `${formato} ${horas}:${minutos}`;
        }

        return formato;
    }, [empresaConfig.formatacao]);

    // =============================================
    // 🔥 VALOR A SUBMETER
    // =============================================
    const valorCalculadoSubmissao = useMemo(() => {
        const ap = dados?.dados?.apuramentoIVA || {};

        if (isISPC) {
            const { imposto } = getImpostoTrimestreISPC(trimestreAtual);
            return Number(imposto.toFixed(2));
        }

        if (isNormal) {
            return Number((ap.ivaAEntregar || 0).toFixed(2));
        }

        return 0;
    }, [isISPC, isNormal, trimestreAtual, dados, getImpostoTrimestreISPC]);

    const detalhesCalculo = useMemo(() => {
        const ap = dados?.dados?.apuramentoIVA || {};

        if (isISPC) {
            const { imposto, volume, fecho, isRegistado } = getImpostoTrimestreISPC(trimestreAtual);
            return {
                tipo: 'ISPC',
                baseLabel: isRegistado ? 'Volume Registado' : `Volume do T${trimestreAtual}`,
                base: volume,
                taxa: TAXA_ISPC,
                taxaLabel: TAXA_ISPC_LABEL,
                formula: `${formatarMoedaLocal(volume)} × ${TAXA_ISPC_LABEL}`,
                fecho,
                isRegistado
            };
        }

        if (isNormal) {
            return {
                tipo: 'IVA',
                baseLabel: 'IVA a Entregar',
                base: ap.ivaAEntregar || 0,
                taxa: null,
                taxaLabel: taxaLabel,
                formula: `IVA Liquidado − IVA Dedutível (Despesas + Stock + Compras)`
            };
        }

        return null;
    }, [isISPC, isNormal, trimestreAtual, dados, taxaLabel, formatarMoedaLocal, getImpostoTrimestreISPC]);

    // =============================================
    // 🔥 APLICAR CONFIGURAÇÃO DA EMPRESA (helper)
    // =============================================
    const aplicarConfiguracao = useCallback((resData) => {
        if (!resData) return;
        const cfg = resData.configuracao;
        const emp = resData.empresa;

        if (cfg || emp) {
            setEmpresaConfig(prev => ({
                ...prev,
                regimeIVA: emp?.regimeIVA ?? prev.regimeIVA,
                percentagemIVA: emp?.percentagemIVA ?? prev.percentagemIVA,
                moeda: cfg?.moeda || emp?.moeda || prev.moeda,
                formatacao: cfg?.formatacao || emp?.formatacao || prev.formatacao
            }));
        }
    }, []);

    // =============================================
    // CARREGAR FECHOS
    // =============================================
    const carregarFechos = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/contabilidade/fechos`, {
                params: { ano },
                headers
            });
            if (res.data?.success) {
                aplicarConfiguracao(res.data);

                const lista = res.data.fechos || [];
                console.log('🔍 [Dashboard] Fechos carregados:', lista.length, 'para ano', ano);
                setFechos(lista);
                return lista;
            }
            return [];
        } catch (e) {
            console.error('❌ Erro ao carregar fechos:', e);
            return [];
        }
    }, [ano, headers, aplicarConfiguracao]);

    // =============================================
    // 🔥 CARREGAR DADOS (resumo + comparativo + fechos)
    // =============================================
    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const [resResumo, resComparativo] = await Promise.all([
                axios.get(`${API}/contabilidade/resumo`, {
                    params: { ano, mes, tipo }, headers
                }),
                axios.get(`${API}/contabilidade/comparativo-anual`, {
                    params: { ano }, headers
                })
            ]);

            if (resResumo.data.success) {
                setDados(resResumo.data);
                aplicarConfiguracao(resResumo.data);
            }
            if (resComparativo.data.success) {
                setComparativo(resComparativo.data.meses || []);
                aplicarConfiguracao(resComparativo.data);
            }

            await carregarFechos();
        } catch (e) {
            console.error('❌ Erro carregar dashboard:', e);
        } finally {
            setLoading(false);
        }
    }, [ano, mes, tipo, headers, carregarFechos, aplicarConfiguracao]);

    useEffect(() => { carregar(); }, [carregar]);

    useEffect(() => {
        carregarFechos();
    }, [ano, carregarFechos]);

    // =============================================
    // 🔥 FECHAR PERÍODO
    // =============================================
    const fecharPeriodo = () => {
        if (isISPC && !isMesFimTrimestre(mes)) {
            mostrarPopup('warning', 'Período inválido para ISPC',
                `No regime ISPC, o fecho só é possível em Março, Junho, Setembro ou Dezembro.\nMês atual: ${meses[mes]}`);
            return;
        }
        if (isIsento) {
            mostrarPopup('info', 'Regime Isento',
                'Empresas no regime Isento não têm fecho de IVA.');
            return;
        }

        confirmarPopup(
            'Fechar Período',
            `Tem a certeza que deseja fechar o período ${meses[mes]} ${ano}?\n\nEsta ação não pode ser desfeita.`,
            async () => {
                setFechando(true);
                try {
                    await axios.post(`${API}/contabilidade/fechar`,
                        { ano, mes, tipo },
                        { headers });
                    mostrarPopup('success', 'Período Fechado',
                        `O período ${meses[mes]} ${ano} foi fechado com sucesso.`);
                    carregar();
                } catch (e) {
                    mostrarPopup('danger', 'Erro ao Fechar',
                        e.response?.data?.message || 'Erro ao fechar o período. Tente novamente.');
                } finally {
                    setFechando(false);
                }
            },
            { textoConfirmar: 'Fechar Período', tipo: 'warning' }
        );
    };

    // =============================================
    // 🔥 REGISTAR / EDITAR SUBMISSÃO
    // =============================================
    const registarSubmissao = async () => {
        if (!dados?.fechoExistente?._id) {
            mostrarPopup('warning', 'Período não fechado', 'É preciso ter o período fechado primeiro.');
            return;
        }

        if (isIsento) {
            mostrarPopup('info', 'Regime Isento', 'Empresas no regime Isento não têm submissão de IVA.');
            return;
        }

        if (submissaoCompleta) {
            mostrarPopup('info', 'Submissão Completa',
                'Esta submissão já está paga e com comprovativo. Não pode ser editada.');
            return;
        }

        if (isISPC && !isMesFimTrimestre(mes)) {
            mostrarPopup('warning', 'Período inválido para ISPC',
                `No regime ISPC, a submissão só é possível em Março, Junho, Setembro ou Dezembro.\nPeríodo selecionado: ${meses[mes]} ${ano}`);
            return;
        }

        if (!submissaoForm.referencia || submissaoForm.referencia.trim() === '') {
            mostrarPopup('warning', 'Campo obrigatório', 'A referência / nº da guia é obrigatória.');
            return;
        }

        if (!submissaoForm.dataSubmissao) {
            mostrarPopup('warning', 'Campo obrigatório', 'A data de submissão é obrigatória.');
            return;
        }

        const estadoFormLower = String(submissaoForm.estado).toLowerCase();
        const temComprovativoExistente = !!dados?.fechoExistente?.submissaoFinancas?.comprovativoUrl;
        const temNovoComprovativo = !!submissaoFicheiro;

        if (estadoFormLower === 'pago' && !temComprovativoExistente && !temNovoComprovativo) {
            mostrarPopup(
                'warning',
                'Comprovativo obrigatório',
                'Para marcar como PAGO é obrigatório anexar o comprovativo de pagamento.\n\n' +
                'Anexe o ficheiro ou altere o estado para "Submetido".'
            );
            return;
        }

        const isEdicao = estadoSubmissao !== ESTADOS_SUBMISSAO.SEM_SUBMISSAO;
        const tituloConfirmacao = isEdicao ? 'Confirmar Edição' : 'Confirmar Submissão';

        let descricaoAcao = 'registar';
        if (faltaComprovativo) descricaoAcao = 'anexar comprovativo e completar';
        else if (isEdicao) descricaoAcao = 'atualizar';

        confirmarPopup(
            tituloConfirmacao,
            `Confirma ${descricaoAcao} da submissão de ${tipoImposto} do período ${tituloPeriodo}?\n\n` +
            `Referência: ${submissaoForm.referencia}\n` +
            `Valor: ${formatarMoeda(submissaoForm.valorPago || 0)}\n` +
            `Estado: ${submissaoForm.estado}` +
            (temNovoComprovativo ? `\n\n📎 Comprovativo: ${submissaoFicheiro.name}` : ''),
            async () => {
                setSubmetendo(true);
                try {
                    const formData = new FormData();
                    formData.append('referencia', submissaoForm.referencia);
                    formData.append('dataSubmissao', submissaoForm.dataSubmissao);
                    if (submissaoForm.dataPagamento) formData.append('dataPagamento', submissaoForm.dataPagamento);
                    formData.append('valorPago', submissaoForm.valorPago || '0');
                    formData.append('estado', submissaoForm.estado);
                    formData.append('observacoes', submissaoForm.observacoes);
                    formData.append('tipoImposto', tipoImposto);
                    formData.append('regimeIVA', regimeIVA);
                    if (submissaoFicheiro) formData.append('comprovativo', submissaoFicheiro);

                    await axios.put(
                        `${API}/api/contabilidade/fechos/${dados.fechoExistente._id}/submissao`,
                        formData,
                        {
                            headers: {
                                ...headers,
                                'Content-Type': 'multipart/form-data'
                            }
                        }
                    );

                    setModalSubmissaoAberto(false);
                    setSubmissaoFicheiro(null);

                    let tituloSucesso = 'Submissão Registada';
                    let msgSucesso = `A submissão de ${tipoImposto} do período ${tituloPeriodo} foi registada com sucesso.`;

                    if (faltaComprovativo) {
                        tituloSucesso = 'Comprovativo Anexado';
                        msgSucesso = `O comprovativo foi anexado com sucesso. A submissão de ${tipoImposto} do período ${tituloPeriodo} está agora completa.`;
                    } else if (isEdicao) {
                        tituloSucesso = 'Submissão Atualizada';
                        msgSucesso = `A submissão de ${tipoImposto} do período ${tituloPeriodo} foi atualizada com sucesso.`;
                    }

                    mostrarPopup('success', tituloSucesso, msgSucesso);
                    carregar();
                } catch (e) {
                    mostrarPopup('danger', 'Erro ao Registar',
                        e.response?.data?.message || 'Erro ao registar submissão. Tente novamente.');
                } finally {
                    setSubmetendo(false);
                }
            },
            { textoConfirmar: isEdicao ? 'Confirmar Edição' : 'Confirmar Submissão', tipo: 'confirm' }
        );
    };

    // =============================================
    // 🔥 ABRIR MODAL DE SUBMISSÃO
    // =============================================
    const abrirModalSubmissao = () => {
        if (isIsento) {
            mostrarPopup('info', 'Regime Isento', 'Empresas no regime Isento não têm submissão de IVA.');
            return;
        }

        if (submissaoCompleta) {
            mostrarPopup('info', 'Submissão Completa',
                'Esta submissão já está paga e com comprovativo. Não pode ser editada.');
            return;
        }

        if (isISPC && !isMesFimTrimestre(mes)) {
            mostrarPopup('warning', 'Período inválido para ISPC',
                `No regime ISPC, a submissão só é possível em Março, Junho, Setembro ou Dezembro.\nPeríodo selecionado: ${meses[mes]} ${ano}`);
            return;
        }

        const sub = dados?.fechoExistente?.submissaoFinancas || {};

        const anoAtual = ano;
        const refDefault = isISPC
            ? `ISPC-${anoAtual}-T${trimestreAtual}`
            : `IVA-${anoAtual}-${String(mes + 1).padStart(2, '0')}`;

        const valorPreenchido = sub.valorPago
            ? sub.valorPago
            : (valorCalculadoSubmissao > 0 ? valorCalculadoSubmissao : '');

        let estadoInicial = sub.estado || 'Submetido';
        if (faltaComprovativo) estadoInicial = 'Pago';

        setSubmissaoForm({
            referencia: sub.referencia || refDefault,
            dataSubmissao: sub.dataSubmissao
                ? new Date(sub.dataSubmissao).toISOString().slice(0, 10)
                : new Date().toISOString().slice(0, 10),
            dataPagamento: sub.dataPagamento
                ? new Date(sub.dataPagamento).toISOString().slice(0, 10)
                : '',
            valorPago: valorPreenchido,
            estado: estadoInicial,
            observacoes: sub.observacoes || ''
        });
        setSubmissaoFicheiro(null);
        setModalSubmissaoAberto(true);
    };

    const d = dados?.dados || {};
    const r = d.receitas || {};
    const des = d.despesas || {};
    const ap = d.apuramentoIVA || {};
    const res = d.resultado || {};
    // 🔥 NOVO: bloco de compras a fornecedor
    const compras = d.compras || {
        totalCompras: 0,
        totalPagoCompras: 0,
        totalAPagar: 0,
        numeroCompras: 0,
        baseTributavel: 0,
        ivaDedutivel: 0
    };

    const ivaPorTaxa = r.apuramentoIVAPorTaxa || [];
    const despesasPorCat = des.porCategoria || [];

    const tituloPeriodo = isISPC
        ? `T${trimestreAtual}/${ano}`
        : `${meses[mes]} ${ano}`;

    // =============================================
    // 🔥 IMPOSTO REAL DO PERÍODO ATUAL
    // =============================================
    const impostoPeriodoAtual = useMemo(() => {
        if (isISPC) {
            const { imposto, volume, fecho, isRegistado } = getImpostoTrimestreISPC(trimestreAtual);
            return {
                valor: imposto,
                volume,
                fecho,
                isRegistado,
                label: 'ISPC',
                taxa: TAXA_ISPC_LABEL
            };
        }
        return {
            valor: ap.ivaAEntregar || 0,
            label: 'IVA',
            taxa: taxaLabel
        };
    }, [isISPC, ap, trimestreAtual, taxaLabel, getImpostoTrimestreISPC]);

    // 🔥 Flag "tem compras" para renderização condicional
    const temCompras = (compras.numeroCompras || 0) > 0;

    const popupConfig = {
        info: { iconeName: 'info', cor: '#3b82f6', bgCor: 'rgba(59, 130, 246, 0.08)' },
        success: { iconeName: 'check_circle', cor: '#10b981', bgCor: 'rgba(16, 185, 129, 0.08)' },
        warning: { iconeName: 'warning', cor: '#f59e0b', bgCor: 'rgba(245, 158, 11, 0.08)' },
        danger: { iconeName: 'error', cor: '#dc2626', bgCor: 'rgba(220, 38, 38, 0.08)' },
        confirm: { iconeName: 'schedule', cor: '#4f46e5', bgCor: 'rgba(79, 70, 229, 0.08)' }
    }[popup.tipo] || { iconeName: 'info', cor: '#3b82f6', bgCor: 'rgba(59, 130, 246, 0.08)' };

    const estadoFormLower = String(submissaoForm.estado).toLowerCase();
    const alertaComprovativoObrigatorio = estadoFormLower === 'pago'
        && !submissaoFicheiro
        && !dados?.fechoExistente?.submissaoFinancas?.comprovativoUrl;

    // =============================================
    // 🔥 DEBUG
    // =============================================
    useEffect(() => {
        if (isISPC && comparativo.length > 0) {
            const t1 = getImpostoTrimestreISPC(1);
            const t2 = getImpostoTrimestreISPC(2);
            const t3 = getImpostoTrimestreISPC(3);
            const t4 = getImpostoTrimestreISPC(4);
            console.log('🔍 [Dashboard] Impostos por trimestre:', {
                T1: t1.imposto, T1_registado: t1.isRegistado,
                T2: t2.imposto, T2_registado: t2.isRegistado,
                T3: t3.imposto, T3_registado: t3.isRegistado,
                T4: t4.imposto, T4_registado: t4.isRegistado
            });
        }
    }, [isISPC, comparativo, fechos, getImpostoTrimestreISPC]);

    return (
        <div className="contab-dash">
            <header className="contab-header">
                <div>
                    <h1>💰 Painel Contabilístico</h1>
                    <p>IVA • Compras • Base Tributável • Lucro • Finanças</p>
                </div>
                <div className="contab-filtros">
                    <span
                        className="contab-badge-regime"
                        title={`Regime ${regimeIVA} — Submissão ${periodicidadeLabel}`}
                    >
                        Regime {regimeIVA} · {periodicidadeLabel} · {taxaLabel}
                    </span>

                    <select value={tipo} onChange={e => setTipo(e.target.value)}>
                        <option value="Mensal">Mensal</option>
                        <option value="Trimestral">Trimestral</option>
                        <option value="Anual">Anual</option>
                    </select>

                    {tipo === 'Mensal' && (
                        <select value={mes} onChange={e => setMes(parseInt(e.target.value))}>
                            {meses.map((m, i) => (
                                <option key={i} value={i}>
                                    {m}
                                    {isISPC && MESES_FIM_TRIMESTRE.includes(i) && ' ✓'}
                                </option>
                            ))}
                        </select>
                    )}

                    <select value={ano} onChange={e => setAno(parseInt(e.target.value))}>
                        {[2023, 2024, 2025, 2026, 2027].map(a => <option key={a} value={a}>{a}</option>)}
                    </select>

                    <button
                        className="btn-fechar"
                        onClick={fecharPeriodo}
                        disabled={
                            fechando ||
                            !!dados?.fechoExistente ||
                            isIsento ||
                            (isISPC && !isMesFimTrimestre(mes))
                        }
                        title={
                            isIsento
                                ? 'Empresa isenta — sem fecho de IVA'
                                : isISPC && !isMesFimTrimestre(mes)
                                    ? `Regime ISPC: fecho só em Março, Junho, Setembro ou Dezembro`
                                    : dados?.fechoExistente
                                        ? 'Período já fechado'
                                        : 'Fechar período'
                        }
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.35rem'
                        }}
                    >
                        <Icon name="lock" size={14} color="currentColor" />
                        {fechando ? 'A fechar...' : 'Fechar Período'}
                    </button>

                    <button
                        className={botaoSubmissaoConfig.classe}
                        onClick={abrirModalSubmissao}
                        disabled={botaoSubmissaoConfig.disabled}
                        title={botaoSubmissaoConfig.title}
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.35rem',
                            position: 'relative'
                        }}
                    >
                        <Icon name={botaoSubmissaoConfig.icone} size={14} color="currentColor" />
                        {botaoSubmissaoConfig.texto}
                        {botaoSubmissaoConfig.iconeAlerta && (
                            <span
                                className="btn-alerta-icone"
                                title="Ação necessária"
                            >
                                <Icon
                                    name={botaoSubmissaoConfig.iconeAlerta}
                                    size={14}
                                    color="#fff"
                                />
                            </span>
                        )}
                    </button>
                </div>
            </header>

            {dados?.fechoExistente && dados.fechoExistente.fechadoEm && (
                <div className="badge-fechado" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}>
                    <Icon name="check_circle" size={14} color="#10b981" />
                    Período já fechado em{' '}
                    {formatarData(dados.fechoExistente.fechadoEm)}
                    {dados.fechoExistente.submissaoFinancas?.estado
                        && dados.fechoExistente.submissaoFinancas.estado !== 'Pendente' && (
                            <>
                                {' • '}
                                <strong>Finanças: {dados.fechoExistente.submissaoFinancas.estado}</strong>
                            </>
                        )}
                </div>
            )}

            {!loading && isISPC && (
                <div className="contab-alerta-info warning">
                    <span className="alerta-icone"><Icon name="info" size={18} color="#d97706" /></span>
                    <div className="alerta-conteudo">
                        <strong>Regime ISPC — Apuramento Trimestral a {TAXA_ISPC_LABEL}</strong>
                        <span>
                            Esta empresa <strong>não cobra IVA nas faturas (0%)</strong> e paga{' '}
                            <strong>{TAXA_ISPC_LABEL} sobre o Volume de Negócios</strong>,
                            submetido a cada 3 meses — <strong>Março, Junho, Setembro e Dezembro</strong>.
                            Período atual: <strong>{tituloPeriodo}</strong>.
                        </span>
                    </div>
                </div>
            )}

            {!loading && isIsento && (
                <div className="contab-alerta-info info">
                    <span className="alerta-icone"><Icon name="info" size={18} color="#3b82f6" /></span>
                    <div className="alerta-conteudo">
                        <strong>Regime Isento</strong>
                        <span>
                            Esta empresa está no regime Isento e não tem apuramento nem submissão de IVA.
                        </span>
                    </div>
                </div>
            )}

            {loading ? (
                <p className="vazio">A carregar...</p>
            ) : isIsento ? (
                <div className="contab-empty">
                    <Icon name="block" size={48} color="var(--fin-muted)" />
                    <h3>Empresa Isenta de IVA</h3>
                    <p>Não há apuramento nem submissão de IVA para este regime.</p>
                </div>
            ) : (
                <>
                    {/* ============ CARDS KPI — VENDAS + IVA ============ */}
                    <section className="contab-cards">
                        <div className="contab-card azul">
                            <span>Receita Bruta</span>
                            <strong>{formatarMoeda(r.baseTributavel)}</strong>
                            <small>{r.numeroFaturas || 0} faturas</small>
                        </div>
                        <div className="contab-card verde">
                            <span>Total Recebido</span>
                            <strong>{formatarMoeda(r.recebidoNoPeriodo)}</strong>
                            <small>no período</small>
                        </div>
                        <div className="contab-card laranja">
                            <span>Em Dívida</span>
                            <strong>{formatarMoeda(r.emDivida)}</strong>
                            <small>a receber</small>
                        </div>

                        <div className="contab-card vermelho">
                            <span>
                                {isISPC
                                    ? `Imposto ISPC a Entregar (T${trimestreAtual})`
                                    : 'IVA a Entregar'}
                            </span>
                            <strong>{formatarMoeda(impostoPeriodoAtual.valor)}</strong>
                            <small>
                                {isISPC
                                    ? impostoPeriodoAtual.isRegistado
                                        ? `${TAXA_ISPC_LABEL} · valor registado`
                                        : `${TAXA_ISPC_LABEL} sobre volume`
                                    : 'após dedução'}
                            </small>
                        </div>

                        <div className="contab-card roxo">
                            <span>Lucro Líquido</span>
                            <strong>{formatarMoeda(res.lucroLiquido)}</strong>
                            <small>margem {res.margemLucro}%</small>
                        </div>
                        <div className="contab-card cinza">
                            <span>IRC Estimado</span>
                            <strong>{formatarMoeda(res.ircEstimado)}</strong>
                            <small>32% sobre lucro</small>
                        </div>
                    </section>

                    {/* ============ 🔥 CARDS KPI — COMPRAS A FORNECEDOR ============ */}
                    <section className="contab-cards">
                        <div className="contab-card azul-claro">
                            <span>Total Compras</span>
                            <strong>{formatarMoeda(compras.totalCompras)}</strong>
                            <small>{compras.numeroCompras || 0} compras</small>
                        </div>
                        <div className="contab-card turquesa">
                            <span>Pago a Fornecedores</span>
                            <strong>{formatarMoeda(compras.totalPagoCompras)}</strong>
                            <small>total liquidado</small>
                        </div>
                        <div className="contab-card laranja-escuro">
                            <span>A Pagar a Fornecedores</span>
                            <strong>{formatarMoeda(compras.totalAPagar)}</strong>
                            <small>saldo em aberto</small>
                        </div>
                        <div className="contab-card roxo-claro">
                            <span>IVA Dedutível (Compras)</span>
                            <strong>{formatarMoeda(compras.ivaDedutivel)}</strong>
                            <small>IVA suportado</small>
                        </div>
                        <div className="contab-card azul-claro">
                            <span>Base Tributável (Compras)</span>
                            <strong>{formatarMoeda(compras.baseTributavel)}</strong>
                            <small>subtotal</small>
                        </div>
                        <div className="contab-card verde-escuro">
                            <span>IVA Dedutível (Stock)</span>
                            <strong>{formatarMoeda(des.ivaDedutivelStock || 0)}</strong>
                            <small>via entradas de produto</small>
                        </div>
                    </section>

                    {/* ============ PAINEL — APURAMENTO IVA ============ */}
                    <section className="contab-panel">
                        <h3>
                            🧾 {isISPC ? 'Apuramento ISPC' : 'Apuramento de IVA'}
                            {isISPC && (
                                <span style={{
                                    marginLeft: '0.5rem',
                                    fontSize: '0.7rem',
                                    fontWeight: 500,
                                    color: 'var(--ct-text-muted)'
                                }}>
                                    — {tituloPeriodo}
                                </span>
                            )}
                        </h3>
                        {isISPC ? (
                            <div className="iva-grid">
                                <div className="iva-item">
                                    <span>Volume de Negócios</span>
                                    <strong>{formatarMoeda(r.baseTributavel)}</strong>
                                    <small style={{ fontSize: '0.6rem', opacity: 0.7 }}>
                                        {r.numeroFaturas || 0} faturas
                                    </small>
                                </div>
                                <div className="iva-item">
                                    <span>Taxa ISPC</span>
                                    <strong>{TAXA_ISPC_LABEL}</strong>
                                    <small style={{ fontSize: '0.6rem', opacity: 0.7 }}>
                                        sobre volume trimestral
                                    </small>
                                </div>
                                <div className={`iva-item ${impostoPeriodoAtual.valor > 0 ? 'destaque' : ''}`}>
                                    <span>Imposto a Entregar</span>
                                    <strong>{formatarMoeda(impostoPeriodoAtual.valor)}</strong>
                                </div>
                                <div className="iva-item">
                                    <span>Periodicidade</span>
                                    <strong style={{ fontSize: '0.95rem' }}>Trimestral</strong>
                                    <small style={{ fontSize: '0.6rem', opacity: 0.7 }}>
                                        Mar / Jun / Set / Dez
                                    </small>
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* 🔥 Apuramento IVA decompondo as 3 fontes de dedutível */}
                                <div className="iva-grid">
                                    <div className="iva-item">
                                        <span>IVA Liquidado (Vendas)</span>
                                        <strong>{formatarMoeda(ap.ivaLiquidado)}</strong>
                                    </div>
                                    <div className="iva-item">
                                        <span>IVA Dedutível — Despesas</span>
                                        <strong>{formatarMoeda(ap.ivaDedutivelDespesas || 0)}</strong>
                                    </div>
                                    <div className="iva-item">
                                        <span>IVA Dedutível — Stock</span>
                                        <strong>{formatarMoeda(ap.ivaDedutivelStock || 0)}</strong>
                                    </div>
                                    <div className="iva-item">
                                        <span>IVA Dedutível — Compras</span>
                                        <strong>{formatarMoeda(ap.ivaDedutivelCompras || 0)}</strong>
                                    </div>
                                </div>

                                {/* Segunda linha: totais + IVA a entregar/recuperar */}
                                <div className="iva-grid">
                                    <div className="iva-item">
                                        <span>IVA Dedutível Total</span>
                                        <strong>{formatarMoeda(ap.ivaDedutivel || 0)}</strong>
                                    </div>
                                    <div className={`iva-item ${ap.ivaAEntregar > 0 ? 'destaque' : ''}`}>
                                        <span>IVA a Entregar</span>
                                        <strong>{formatarMoeda(ap.ivaAEntregar)}</strong>
                                    </div>
                                    <div className={`iva-item ${ap.ivaARecuperar > 0 ? 'recuperar' : ''}`}>
                                        <span>IVA a Recuperar</span>
                                        <strong>{formatarMoeda(ap.ivaARecuperar)}</strong>
                                    </div>
                                    <div className="iva-item">
                                        <span>Saldo do Período</span>
                                        <strong>{formatarMoeda(ap.saldoFinal || 0)}</strong>
                                    </div>
                                </div>
                            </>
                        )}
                    </section>

                    {/* ============ PAINEL — COMPRAS A FORNECEDOR ============ */}
                    <section className="contab-panel">
                        <h3>
                            🚚 Compras a Fornecedor
                            <span style={{
                                marginLeft: '0.5rem',
                                fontSize: '0.7rem',
                                fontWeight: 500,
                                color: 'var(--ct-text-muted)'
                            }}>
                                — {tituloPeriodo}
                            </span>
                        </h3>
                        {!temCompras ? (
                            <p className="vazio">Sem compras registadas para o período</p>
                        ) : (
                            <>
                                <div className="iva-grid">
                                    <div className="iva-item">
                                        <span>Total de Compras</span>
                                        <strong>{formatarMoeda(compras.totalCompras)}</strong>
                                        <small style={{ fontSize: '0.6rem', opacity: 0.7 }}>
                                            {compras.numeroCompras} compras
                                        </small>
                                    </div>
                                    <div className="iva-item">
                                        <span>Pago a Fornecedores</span>
                                        <strong>{formatarMoeda(compras.totalPagoCompras)}</strong>
                                    </div>
                                    <div className="iva-item">
                                        <span>A Pagar a Fornecedores</span>
                                        <strong>{formatarMoeda(compras.totalAPagar)}</strong>
                                    </div>
                                    <div className="iva-item destaque">
                                        <span>IVA Dedutível (Compras)</span>
                                        <strong>{formatarMoeda(compras.ivaDedutivel)}</strong>
                                    </div>
                                </div>

                                <div className="tabela-wrapper" style={{ marginTop: '0.75rem' }}>
                                    <table className="contab-tabela">
                                        <thead>
                                            <tr>
                                                <th>Indicador</th>
                                                <th>Valor</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td><strong>Base Tributável (Compras)</strong></td>
                                                <td>{formatarMoeda(compras.baseTributavel)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>IVA Dedutível (Compras)</strong></td>
                                                <td>{formatarMoeda(compras.ivaDedutivel)}</td>
                                            </tr>
                                            <tr>
                                                <td><strong>Número de Compras</strong></td>
                                                <td>{compras.numeroCompras}</td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </section>

                    {/* ============ GRÁFICOS ============ */}
                    <section className="contab-grid">
                        <div className="contab-panel">
                            <h3>{isISPC ? 'Volume por Mês' : 'IVA por Taxa'}</h3>
                            {isISPC ? (
                                comparativo.length === 0 ? (
                                    <p className="vazio">Sem dados</p>
                                ) : (
                                    <ResponsiveContainer width="100%" height={280}>
                                        <BarChart data={comparativo}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="mes" />
                                            <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                                            <Tooltip formatter={(v) => formatarMoeda(v)} />
                                            <Legend />
                                            <Bar dataKey="faturado" fill="#3b82f6" name="Volume" radius={[6, 6, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                )
                            ) : ivaPorTaxa.length === 0 ? (
                                <p className="vazio">Sem dados</p>
                            ) : (
                                <ResponsiveContainer width="100%" height={280}>
                                    <BarChart data={ivaPorTaxa}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="taxa" tickFormatter={(v) => `${v}%`} />
                                        <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                                        <Tooltip formatter={(v) => formatarMoeda(v)} />
                                        <Legend />
                                        <Bar dataKey="base" fill="#3b82f6" name="Base" radius={[6, 6, 0, 0]} />
                                        <Bar dataKey="iva" fill="#ef4444" name="IVA" radius={[6, 6, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </div>

                        <div className="contab-panel">
                            <h3>Despesas por Categoria</h3>
                            {despesasPorCat.length === 0 ? (
                                <p className="vazio">Sem dados</p>
                            ) : (
                                <ResponsiveContainer width="100%" height={280}>
                                    <PieChart>
                                        <Pie
                                            data={despesasPorCat}
                                            dataKey="valor"
                                            nameKey="categoria"
                                            cx="50%"
                                            cy="45%"
                                            outerRadius="70%"
                                            innerRadius="40%"
                                            paddingAngle={2}
                                            labelLine={{
                                                stroke: 'var(--ct-border)',
                                                strokeWidth: 1
                                            }}
                                            label={({ percent }) => {
                                                if (percent < 0.05) return null;
                                                return `${(percent * 100).toFixed(0)}%`;
                                            }}
                                        >
                                            {despesasPorCat.map((_, i) => (
                                                <Cell
                                                    key={i}
                                                    fill={CORES[i % CORES.length]}
                                                    stroke="var(--ct-white)"
                                                    strokeWidth={2}
                                                />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(v) => formatarMoeda(v)} />
                                        <Legend
                                            verticalAlign="bottom"
                                            height={36}
                                            iconType="circle"
                                            iconSize={8}
                                            wrapperStyle={{ fontSize: '12px', paddingTop: '8px' }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            )}
                        </div>
                    </section>

                    {/* ============ 🔥 EVOLUÇÃO ANUAL — INCLUI COMPRAS ============ */}
                    <section className="contab-panel">
                        <h3>📈 Evolução Anual ({ano})</h3>
                        <ResponsiveContainer width="100%" height={320}>
                            <LineChart data={comparativo}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="mes" />
                                <YAxis tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                                <Tooltip formatter={(v) => formatarMoeda(v)} />
                                <Legend />
                                <Line type="monotone" dataKey="faturado" stroke="#3b82f6" name="Faturado" strokeWidth={2} />
                                <Line type="monotone" dataKey="recebido" stroke="#10b981" name="Recebido" strokeWidth={2} />
                                <Line type="monotone" dataKey="despesas" stroke="#ef4444" name="Despesas" strokeWidth={2} />
                                {/* 🔥 NOVO: linha de compras a fornecedor */}
                                <Line type="monotone" dataKey="compras" stroke="#f97316" name="Compras" strokeWidth={2} />
                                <Line type="monotone" dataKey="lucroLiquido" stroke="#8b5cf6" name="Lucro Líquido" strokeWidth={2} />
                            </LineChart>
                        </ResponsiveContainer>
                    </section>

                    {/* ============ TABELA DETALHE IVA POR TAXA ============ */}
                    {!isISPC && (
                        <section className="contab-panel">
                            <h3>Detalhe IVA por Taxa</h3>
                            <table className="contab-tabela">
                                <thead>
                                    <tr>
                                        <th>Taxa</th>
                                        <th>Base Tributável</th>
                                        <th>IVA</th>
                                        <th>Nº Faturas</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ivaPorTaxa.length === 0 && (
                                        <tr><td colSpan="4" className="vazio">Sem dados</td></tr>
                                    )}
                                    {ivaPorTaxa.map((t, i) => (
                                        <tr key={i}>
                                            <td><strong>{t.taxa}%</strong></td>
                                            <td>{formatarMoeda(t.base)}</td>
                                            <td>{formatarMoeda(t.iva)}</td>
                                            <td>{t.numeroFaturas}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </section>
                    )}

                    {/* ============ TABELA DE APURAMENTO TRIMESTRAL (ISPC) ============ */}
                    {isISPC && (
                        <section className="contab-panel">
                            <h3>📅 Apuramento por Trimestre ({ano})</h3>
                            <table className="contab-tabela">
                                <thead>
                                    <tr>
                                        <th>Trimestre</th>
                                        <th>Meses</th>
                                        <th>Volume de Negócios</th>
                                        <th>Taxa</th>
                                        <th>Imposto a Entregar</th>
                                        <th>Estado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[1, 2, 3, 4].map((tri) => {
                                        const mesesDoTri = {
                                            1: 'Jan, Fev, Mar',
                                            2: 'Abr, Mai, Jun',
                                            3: 'Jul, Ago, Set',
                                            4: 'Out, Nov, Dez'
                                        }[tri];

                                        const { imposto, volume, fecho, isRegistado } = getImpostoTrimestreISPC(tri);
                                        const estado = estadoDoFecho(fecho);
                                        const badge = estadoVisual(estado);
                                        const temFecho = !!fecho?._id;

                                        return (
                                            <tr key={tri} style={
                                                temFecho
                                                    ? { background: 'rgba(245, 158, 11, 0.04)' }
                                                    : {}
                                            }>
                                                <td><strong>T{tri}/{ano}</strong></td>
                                                <td>{mesesDoTri}</td>
                                                <td>{formatarMoeda(volume)}</td>
                                                <td><strong>{TAXA_ISPC_LABEL}</strong></td>
                                                <td style={{
                                                    fontWeight: 700,
                                                    color: imposto > 0 ? '#dc2626' : 'var(--ct-text-muted)'
                                                }}>
                                                    {imposto > 0 ? formatarMoeda(imposto) : '—'}
                                                    {isRegistado && (
                                                        <span style={{
                                                            marginLeft: '0.35rem',
                                                            fontSize: '0.6rem',
                                                            color: '#10b981',
                                                            fontWeight: 600
                                                        }}>
                                                            ✓
                                                        </span>
                                                    )}
                                                </td>
                                                <td>
                                                    <span className={`fin-badge ${badge.classe}`}>
                                                        <Icon name={badge.icone} size={11} />
                                                        {badge.label}
                                                    </span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr style={{
                                        background: 'rgba(245, 158, 11, 0.08)',
                                        fontWeight: 700
                                    }}>
                                        <td colSpan="2"><strong>TOTAL ANO</strong></td>
                                        <td><strong>{formatarMoeda(r.baseTributavel || 0)}</strong></td>
                                        <td>—</td>
                                        <td style={{ color: '#dc2626' }}>
                                            <strong>
                                                {formatarMoeda([1, 2, 3, 4].reduce((s, tri) => {
                                                    return s + getImpostoTrimestreISPC(tri).imposto;
                                                }, 0))}
                                            </strong>
                                        </td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                            </table>
                            <p style={{
                                marginTop: '0.75rem',
                                fontSize: '0.7rem',
                                color: 'var(--ct-text-muted)',
                                fontStyle: 'italic'
                            }}>
                                ℹ️ No regime ISPC, o imposto é calculado apenas nos meses de
                                Março, Junho, Setembro e Dezembro, aplicando {TAXA_ISPC_LABEL} sobre o
                                Volume de Negócios acumulado do trimestre.
                                {fechos.length > 0 && (
                                    <>
                                        {' '}O símbolo <strong style={{ color: '#10b981' }}>✓</strong> indica
                                        trimestres com fecho registado.
                                    </>
                                )}
                            </p>
                        </section>
                    )}
                </>
            )}

            {/* ===== MODAL DE SUBMISSÃO ===== */}
            {modalSubmissaoAberto && (
                <div
                    className="modal-overlay"
                    onClick={() => !submetendo && setModalSubmissaoAberto(false)}
                >
                    <div className="modal-submissao" onClick={e => e.stopPropagation()}>
                        <header className="modal-submissao-header">
                            <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                <Icon
                                    name={modalIcone}
                                    size={20}
                                    color="var(--ct-primary)"
                                />
                                {modalTitulo}
                                <span className={`badge-tipo-imposto ${tipoImposto.toLowerCase()}`}>
                                    {tipoImposto}
                                </span>
                            </h3>
                            <button
                                className="modal-close"
                                onClick={() => !submetendo && setModalSubmissaoAberto(false)}
                                disabled={submetendo}
                                style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                <Icon name="close" size={18} color="currentColor" />
                            </button>
                        </header>

                        <div className="modal-submissao-body">
                            <div style={{
                                padding: '0.6rem 0.85rem',
                                background: isISPC
                                    ? 'rgba(245, 158, 11, 0.06)'
                                    : 'rgba(79, 70, 229, 0.06)',
                                borderRadius: 'var(--ct-radius)',
                                border: `1px solid ${isISPC ? 'rgba(245, 158, 11, 0.2)' : 'rgba(79, 70, 229, 0.2)'}`,
                                fontSize: '0.72rem',
                                color: isISPC ? '#92400e' : 'var(--ct-primary)',
                                marginBottom: '1rem',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '0.35rem'
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <Icon name="info" size={16} color="currentColor" />
                                    <strong>{descricaoImposto}</strong>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingLeft: '1.4rem' }}>
                                    <span>
                                        {isISPC
                                            ? `Regime ISPC — ${TAXA_ISPC_LABEL} sobre Volume de Negócios — Submissão ${periodicidadeImposto}`
                                            : `Regime Normal — IVA ${taxaLabel} — Submissão ${periodicidadeImposto}`
                                        }
                                    </span>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', paddingLeft: '1.4rem' }}>
                                    <span>
                                        Período: <strong>{tituloPeriodo}</strong>
                                        {isISPC && (
                                            <> • Trimestre: <strong>T{trimestreAtual}/{ano}</strong></>
                                        )}
                                    </span>
                                </div>
                            </div>

                            {detalhesCalculo && (
                                <div className="calculo-automatico-box" style={{
                                    padding: '0.85rem 1rem',
                                    background: 'linear-gradient(135deg, rgba(79, 70, 229, 0.05), rgba(124, 58, 237, 0.03))',
                                    borderRadius: 'var(--ct-radius)',
                                    border: '1px solid rgba(79, 70, 229, 0.15)',
                                    marginBottom: '1rem'
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        marginBottom: '0.5rem',
                                        flexWrap: 'wrap',
                                        gap: '0.5rem'
                                    }}>
                                        <span style={{
                                            fontSize: '0.6rem',
                                            fontWeight: 700,
                                            color: 'var(--ct-primary)',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.35rem'
                                        }}>
                                            <Icon name="analytics" size={14} color="var(--ct-primary)" />
                                            Cálculo Automático
                                            {detalhesCalculo.isRegistado && (
                                                <span style={{
                                                    fontSize: '0.55rem',
                                                    background: 'rgba(16, 185, 129, 0.15)',
                                                    color: '#059669',
                                                    padding: '0.1rem 0.4rem',
                                                    borderRadius: '4px',
                                                    fontWeight: 700
                                                }}>
                                                    REGISTADO
                                                </span>
                                            )}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => setSubmissaoForm({
                                                ...submissaoForm,
                                                valorPago: valorCalculadoSubmissao
                                            })}
                                            style={{
                                                padding: '0.25rem 0.7rem',
                                                background: 'var(--ct-primary)',
                                                color: '#fff',
                                                border: 'none',
                                                borderRadius: '100px',
                                                fontSize: '0.6rem',
                                                fontWeight: 700,
                                                cursor: 'pointer',
                                                fontFamily: 'inherit',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '0.3rem'
                                            }}
                                            title="Voltar a aplicar o valor calculado"
                                        >
                                            <Icon name="refresh" size={12} color="#fff" />
                                            Aplicar
                                        </button>
                                    </div>

                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: isISPC ? 'repeat(3, 1fr)' : '1fr',
                                        gap: '0.5rem',
                                        fontSize: '0.7rem'
                                    }}>
                                        {isISPC && (
                                            <>
                                                <div style={{
                                                    background: 'rgba(255, 255, 255, 0.5)',
                                                    padding: '0.5rem 0.7rem',
                                                    borderRadius: '8px'
                                                }}>
                                                    <div style={{
                                                        fontSize: '0.55rem',
                                                        color: 'var(--ct-text-muted)',
                                                        textTransform: 'uppercase',
                                                        fontWeight: 700,
                                                        marginBottom: '0.15rem'
                                                    }}>
                                                        {detalhesCalculo.baseLabel}
                                                    </div>
                                                    <strong style={{
                                                        fontSize: '0.85rem',
                                                        color: 'var(--ct-text)'
                                                    }}>
                                                        {formatarMoeda(detalhesCalculo.base)}
                                                    </strong>
                                                </div>
                                                <div style={{
                                                    background: 'rgba(255, 255, 255, 0.5)',
                                                    padding: '0.5rem 0.7rem',
                                                    borderRadius: '8px'
                                                }}>
                                                    <div style={{
                                                        fontSize: '0.55rem',
                                                        color: 'var(--ct-text-muted)',
                                                        textTransform: 'uppercase',
                                                        fontWeight: 700,
                                                        marginBottom: '0.15rem'
                                                    }}>
                                                        Taxa
                                                    </div>
                                                    <strong style={{
                                                        fontSize: '0.85rem',
                                                        color: '#d97706'
                                                    }}>
                                                        {detalhesCalculo.taxaLabel}
                                                    </strong>
                                                </div>
                                            </>
                                        )}
                                        <div style={{
                                            background: 'rgba(79, 70, 229, 0.08)',
                                            padding: '0.5rem 0.7rem',
                                            borderRadius: '8px',
                                            border: '1px solid rgba(79, 70, 229, 0.2)'
                                        }}>
                                            <div style={{
                                                fontSize: '0.55rem',
                                                color: 'var(--ct-primary)',
                                                textTransform: 'uppercase',
                                                fontWeight: 700,
                                                marginBottom: '0.15rem'
                                            }}>
                                                Valor a Submeter
                                            </div>
                                            <strong style={{
                                                fontSize: '1rem',
                                                color: 'var(--ct-primary)'
                                            }}>
                                                {formatarMoeda(valorCalculadoSubmissao)}
                                            </strong>
                                        </div>
                                    </div>

                                    <div style={{
                                        marginTop: '0.5rem',
                                        fontSize: '0.6rem',
                                        color: 'var(--ct-text-muted)',
                                        fontStyle: 'italic',
                                        textAlign: 'center'
                                    }}>
                                        {detalhesCalculo.formula}
                                    </div>
                                </div>
                            )}

                            <div className="form-group">
                                <label>Referência / Nº Guia <span style={{ color: 'var(--ct-danger)' }}>*</span></label>
                                <input
                                    type="text"
                                    value={submissaoForm.referencia}
                                    onChange={e => setSubmissaoForm({ ...submissaoForm, referencia: e.target.value })}
                                    placeholder={isISPC ? 'Ex: ISPC-2026-T1' : 'Ex: IVA-2026-01'}
                                />
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Data de Submissão <span style={{ color: 'var(--ct-danger)' }}>*</span></label>
                                    <input
                                        type="date"
                                        value={submissaoForm.dataSubmissao}
                                        onChange={e => setSubmissaoForm({ ...submissaoForm, dataSubmissao: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Data de Pagamento</label>
                                    <input
                                        type="date"
                                        value={submissaoForm.dataPagamento}
                                        onChange={e => setSubmissaoForm({ ...submissaoForm, dataPagamento: e.target.value })}
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label>Valor Pago</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={submissaoForm.valorPago}
                                        onChange={e => setSubmissaoForm({ ...submissaoForm, valorPago: e.target.value })}
                                        placeholder="0.00"
                                    />
                                    <small style={{
                                        fontSize: '0.6rem',
                                        color: 'var(--ct-text-muted)',
                                        marginTop: '0.2rem',
                                        display: 'block',
                                        fontStyle: 'italic'
                                    }}>
                                        Pré-preenchido com o cálculo automático. Pode ajustar.
                                    </small>
                                </div>
                                <div className="form-group">
                                    <label>Estado</label>
                                    <select
                                        value={submissaoForm.estado}
                                        onChange={e => setSubmissaoForm({ ...submissaoForm, estado: e.target.value })}
                                    >
                                        <option value="Pendente">Pendente</option>
                                        <option value="Submetido">Submetido</option>
                                        <option value="Pago">Pago</option>
                                        <option value="Isento">Isento</option>
                                    </select>
                                    {alertaComprovativoObrigatorio && (
                                        <small style={{
                                            fontSize: '0.6rem',
                                            color: '#d97706',
                                            marginTop: '0.3rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.25rem',
                                            fontWeight: 600
                                        }}>
                                            <Icon name="warning" size={11} color="#d97706" />
                                            Comprovativo obrigatório para estado "Pago"
                                        </small>
                                    )}
                                </div>
                            </div>

                            <div className="form-group">
                                <label>Observações</label>
                                <textarea
                                    value={submissaoForm.observacoes}
                                    onChange={e => setSubmissaoForm({ ...submissaoForm, observacoes: e.target.value })}
                                    rows={3}
                                    placeholder="Notas adicionais..."
                                />
                            </div>

                            <div className="form-group">
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.35rem',
                                    color: alertaComprovativoObrigatorio ? '#d97706' : 'inherit'
                                }}>
                                    Comprovativo de Pagamento
                                    {alertaComprovativoObrigatorio && (
                                        <span style={{
                                            fontSize: '0.55rem',
                                            background: 'rgba(245, 158, 11, 0.15)',
                                            color: '#d97706',
                                            padding: '0.1rem 0.4rem',
                                            borderRadius: '4px',
                                            fontWeight: 700,
                                            textTransform: 'uppercase'
                                        }}>
                                            Obrigatório
                                        </span>
                                    )}
                                </label>
                                <div
                                    className="upload-area"
                                    style={alertaComprovativoObrigatorio ? {
                                        border: '2px dashed #f59e0b',
                                        background: 'rgba(245, 158, 11, 0.04)'
                                    } : {}}
                                >
                                    <input
                                        type="file"
                                        id="comprovativo-input"
                                        accept=".pdf,.jpg,.jpeg,.png,.webp"
                                        onChange={e => setSubmissaoFicheiro(e.target.files[0] || null)}
                                        hidden
                                    />
                                    <label
                                        htmlFor="comprovativo-input"
                                        className="upload-label"
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}
                                    >
                                        <Icon
                                            name="cloud_upload"
                                            size={16}
                                            color={alertaComprovativoObrigatorio ? '#d97706' : 'var(--ct-primary)'}
                                        />
                                        <span>
                                            {submissaoFicheiro
                                                ? submissaoFicheiro.name
                                                : 'Clique para escolher (PDF, JPG, PNG — máx. 10MB)'}
                                        </span>
                                    </label>
                                    {submissaoFicheiro && (
                                        <button
                                            type="button"
                                            className="btn-remover-ficheiro"
                                            onClick={() => setSubmissaoFicheiro(null)}
                                            style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}
                                        >
                                            <Icon name="close" size={12} color="currentColor" />
                                            Remover
                                        </button>
                                    )}
                                </div>
                            </div>

                            {dados?.fechoExistente?.submissaoFinancas?.comprovativoUrl && (
                                <div
                                    className="comprovativo-existente"
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: '0.4rem',
                                        padding: '0.5rem 0.75rem',
                                        background: 'rgba(16, 185, 129, 0.06)',
                                        borderRadius: 'var(--ct-radius)',
                                        border: '1px solid rgba(16, 185, 129, 0.15)'
                                    }}
                                >
                                    <Icon name="check_circle" size={14} color="#10b981" />
                                    <span style={{ fontSize: '0.72rem', color: '#059669', fontWeight: 600 }}>
                                        Comprovativo já anexado
                                    </span>
                                    <a
                                        href={`${API}${dados.fechoExistente.submissaoFinancas.comprovativoUrl}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '0.3rem',
                                            color: 'var(--ct-primary)',
                                            fontWeight: 700,
                                            textDecoration: 'none',
                                            fontSize: '0.72rem'
                                        }}
                                    >
                                        Ver <FaExternalLinkAlt style={{ fontSize: '0.7rem' }} />
                                    </a>
                                </div>
                            )}
                        </div>

                        <footer className="modal-submissao-footer">
                            <button
                                className="btn-cancelar"
                                onClick={() => setModalSubmissaoAberto(false)}
                                disabled={submetendo}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn-confirmar"
                                onClick={registarSubmissao}
                                disabled={submetendo || alertaComprovativoObrigatorio}
                                title={alertaComprovativoObrigatorio ? 'Anexe o comprovativo para continuar' : ''}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
                            >
                                {submetendo
                                    ? 'A submeter...'
                                    : <>
                                        <Icon
                                            name={faltaComprovativo ? 'cloud_upload' : 'check_circle'}
                                            size={14}
                                            color="#fff"
                                        />
                                        {faltaComprovativo ? 'Anexar e Completar' : 'Confirmar Submissão'}
                                    </>}
                            </button>
                        </footer>
                    </div>
                </div>
            )}

            {/* ===== POPUP DO SISTEMA ===== */}
            {popup.aberto && (
                <div
                    className="modal-overlay popup-sistema-overlay"
                    onClick={() => {
                        if (popup.tipo !== 'confirm') fecharPopup();
                    }}
                    style={{ zIndex: 100000 }}
                >
                    <div
                        className="popup-sistema"
                        onClick={e => e.stopPropagation()}
                        style={{
                            maxWidth: '480px',
                            width: '100%',
                            background: 'var(--ct-white, #fff)',
                            borderRadius: '16px',
                            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                            overflow: 'hidden',
                            animation: 'popupSlideIn 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)'
                        }}
                    >
                        <div style={{
                            padding: '1.5rem 1.5rem 1rem',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center',
                            gap: '0.75rem'
                        }}>
                            <div style={{
                                width: '64px',
                                height: '64px',
                                borderRadius: '50%',
                                background: popupConfig.bgCor,
                                color: popupConfig.cor,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: `2px solid ${popupConfig.cor}30`
                            }}>
                                <Icon name={popupConfig.iconeName} size={36} color={popupConfig.cor} />
                            </div>
                            <h3 style={{
                                margin: 0,
                                fontSize: '1.15rem',
                                fontWeight: 800,
                                color: 'var(--ct-text, #1a1a2e)',
                                letterSpacing: '-0.3px'
                            }}>
                                {popup.titulo}
                            </h3>
                        </div>

                        <div style={{
                            padding: '0 1.5rem 1.25rem',
                            textAlign: 'center'
                        }}>
                            <p style={{
                                margin: 0,
                                fontSize: '0.85rem',
                                color: 'var(--ct-text-muted, #6b7280)',
                                lineHeight: 1.55,
                                whiteSpace: 'pre-line'
                            }}>
                                {popup.mensagem}
                            </p>
                        </div>

                        <div style={{
                            padding: '1rem 1.5rem 1.25rem',
                            background: 'var(--ct-light, #f3f4f6)',
                            display: 'flex',
                            gap: '0.6rem',
                            justifyContent: 'center',
                            borderTop: '1px solid var(--ct-border, #e5e7eb)'
                        }}>
                            {popup.textoCancelar && (
                                <button
                                    onClick={popup.onCancelar || fecharPopup}
                                    style={{
                                        padding: '0.6rem 1.5rem',
                                        borderRadius: '100px',
                                        border: '1.5px solid var(--ct-border, #e5e7eb)',
                                        background: 'transparent',
                                        color: 'var(--ct-text-muted, #6b7280)',
                                        fontFamily: 'inherit',
                                        fontSize: '0.78rem',
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        minWidth: '110px'
                                    }}
                                >
                                    {popup.textoCancelar}
                                </button>
                            )}
                            <button
                                onClick={popup.onConfirmar || fecharPopup}
                                style={{
                                    padding: '0.6rem 1.5rem',
                                    borderRadius: '100px',
                                    border: 'none',
                                    background: popupConfig.cor,
                                    color: '#fff',
                                    fontFamily: 'inherit',
                                    fontSize: '0.78rem',
                                    fontWeight: 700,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    boxShadow: `0 2px 10px ${popupConfig.cor}40`,
                                    minWidth: '110px'
                                }}
                            >
                                {popup.textoConfirmar}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes popupSlideIn {
                    from {
                        opacity: 0;
                        transform: translateY(20px) scale(0.95);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0) scale(1);
                    }
                }
                .btn-submissao-completa {
                    background: linear-gradient(135deg, #10b981, #059669) !important;
                    color: #fff !important;
                    cursor: not-allowed !important;
                    opacity: 0.95;
                }
                .btn-submissao-completa:hover {
                    transform: none !important;
                }
                .btn-submissao-editar {
                    background: linear-gradient(135deg, #f59e0b, #d97706) !important;
                    color: #fff !important;
                }
                .btn-submissao-editar:hover {
                    box-shadow: 0 4px 16px rgba(245, 158, 11, 0.35) !important;
                }
                .btn-submissao-pago-sem-doc {
                    background: linear-gradient(135deg, #3b82f6, #2563eb) !important;
                    color: #fff !important;
                    animation: pulsePagoSemDoc 2s ease-in-out infinite;
                }
                .btn-submissao-pago-sem-doc:hover {
                    box-shadow: 0 4px 16px rgba(59, 130, 246, 0.45) !important;
                    animation: none;
                }
                @keyframes pulsePagoSemDoc {
                    0%, 100% {
                        box-shadow: 0 2px 10px rgba(59, 130, 246, 0.3);
                    }
                    50% {
                        box-shadow: 0 4px 20px rgba(59, 130, 246, 0.55);
                    }
                }
                .btn-alerta-icone {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    width: 18px;
                    height: 18px;
                    border-radius: 50%;
                    background: #f59e0b;
                    margin-left: 0.15rem;
                    animation: pulseAlertaIcone 1.5s ease-in-out infinite;
                    box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.7);
                    flex-shrink: 0;
                }
                @keyframes pulseAlertaIcone {
                    0% {
                        box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.7);
                    }
                    70% {
                        box-shadow: 0 0 0 8px rgba(245, 158, 11, 0);
                    }
                    100% {
                        box-shadow: 0 0 0 0 rgba(245, 158, 11, 0);
                    }
                }
                /* 🔥 Cores específicas de Compras — caso não existam em DashboardContabilidade.css */
                .contab-card.azul-claro {
                    border-left-color: #0ea5e9 !important;
                }
                .contab-card.azul-claro strong {
                    color: #0284c7;
                }
                .contab-card.turquesa {
                    border-left-color: #14b8a6 !important;
                }
                .contab-card.turquesa strong {
                    color: #0d9488;
                }
                .contab-card.laranja-escuro {
                    border-left-color: #f97316 !important;
                }
                .contab-card.laranja-escuro strong {
                    color: #ea580c;
                }
                .contab-card.roxo-claro {
                    border-left-color: #a855f7 !important;
                }
                .contab-card.roxo-claro strong {
                    color: #9333ea;
                }
                .contab-card.verde-escuro {
                    border-left-color: #059669 !important;
                }
                .contab-card.verde-escuro strong {
                    color: #047857;
                }
            `}</style>
        </div>
    );
}